import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, combineLatest, forkJoin, throwError } from 'rxjs';
import { map, startWith} from 'rxjs/operators';
import { CommonModule, NgFor, AsyncPipe } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { ResponseService } from 'src/services/response.service';
import { ReplacePatientTermPipe, SanitizeHtmlPipe } from '../filter-section.pipe';
import { SharedDataService } from 'src/services/shared-data.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { tap } from 'rxjs/operators';
import { QuestionnaireResponseAnswer, QuestionnaireResponseQuestion } from 'src/models/questionnaire-response.model';
import { ResponseAnswerUpdateRequest } from 'src/models/response.model';
import { DateUtil } from 'src/app/shared/utils/date.util';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { StringUtil } from '@app/shared/utils/string.util';

@Component({
    selector: 'app-drop-down-list-answer',
    templateUrl: './drop-down-list-answer.component.html',
    styleUrls: ['./drop-down-list-answer.component.css'],
    standalone: true,
    imports: [MaterialModule, CommonModule, FormsModule, ReactiveFormsModule, NgFor, AsyncPipe, ReplacePatientTermPipe, SanitizeHtmlPipe]
})
export class DropDownListAnswerComponent implements OnInit {

  @Input() question!: QuestionnaireResponseQuestion;
  @Output() dropDownValidation = new EventEmitter<any>();
  @Output() dropDownSaved = new EventEmitter<any>();
  @ViewChild(MatAutocomplete) autocomplete!: MatAutocomplete;

  private invalidAutocompleteObjectMessage: string = $localize`:Auto Complete Error Message|Error message displayed if text in the auto complete dropdown does not exist in the list or text an is empty string@autoCompleteValidationErrorMessage:Option not recognized. Select an item from the dropdown.`;
  myInput!: CdkTextareaAutosize;
  isPreview!: boolean
  patientTerm!: string;
  filteredOptions!: Observable<QuestionnaireResponseAnswer[]>;
  selectedOption!: QuestionnaireResponseAnswer | null;
  myControl = new FormControl<string>('', { validators: [this.autocompleteObjectValidator()]});
  placeholderText: string = $localize`:Placeholder text for type here to search| defaul ttext for searchbox@@placeHolderTextForDynamic:Type here to search...`;
  
  constructor(
    private _responseService : ResponseService, 
    private _sharedDataService: SharedDataService
  ) {}
  


  dropDownQuestionValidation = [
    { questionId: '', hasError: false }
  ];

  validation_msgs = {
    'contactAutocompleteControl': [
      { type: 'invalidAutocompleteObject', message: this.invalidAutocompleteObjectMessage}
    ]
  }

  /**
   * 
   * @param value 
   */
  checkDropDownValidation(value: boolean) {  
    (this.dropDownQuestionValidation.filter(e => e.questionId === this.question.questionId).length < 1) ?
      this.dropDownQuestionValidation.push({questionId: this.question.questionId, hasError: value}) :
      this.dropDownQuestionValidation[1].hasError = value;

    this.dropDownValidation.emit(this.dropDownQuestionValidation);
  }

  ngOnInit(): void {
    combineLatest([this._sharedDataService.previewMode$, this._sharedDataService.patientTerm$])
      .subscribe(([isPreview, patientTerm]) => {
        this.isPreview = isPreview
        this.patientTerm = patientTerm
      }
    )

    const selectedAnswer = this.question.answers.find(a => a.selected);
    if(selectedAnswer) {
      this.selectedOption = selectedAnswer
      this.myControl.setValue(this.selectedOption.content.text);
    } else {
      this.selectedOption = null;
      this.myControl.setValue('');
    }

    this.filteredOptions = this.myControl.valueChanges.pipe(
      // trigger the auto complete
      startWith(this.myControl.value),
      map((value: string | null) => {
        if(value===null){
          return this.question.answers
        } else if(typeof value === 'string'){
          return this.question.answers.filter(ans => {
            // remove accents
            const normalizedText: string = StringUtil.normalizeStringLowerCase(ans.content.text)
            const normalizedValue: string = StringUtil.normalizeStringLowerCase(value)
            return normalizedText.includes(normalizedValue)
          })
        } else {
          return [];
        }
      }),
      tap((options: QuestionnaireResponseAnswer[]) => options.sort((a, b) => a.content.text?.toLowerCase() > b.content.text?.toLowerCase() ? 1 : -1))
    );

    this.myControl.valueChanges.subscribe((c) => {
      this.checkDropDownValidation(this.myControl.hasError('invalidAutocompleteObject'));
    })   
  }



  /**
   * 
   * @returns 
   */
  autocompleteObjectValidator(): ValidatorFn{
    
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value === 'string') {
        if (
          !(
            this.question?.answers.some(answer => answer.content.text.toLocaleLowerCase() === control.value.toLocaleLowerCase()) 
            && this.question?.answers.some(answer => answer.selected)
          )) {
          return { 'invalidAutocompleteObject': { value: control.value } }
        }
      }
      return null  /* valid option selected */
    }
  }

  /**
   * 
   * @param selectedAnswer 
   */
  saveSelection(selectedAnswer: QuestionnaireResponseAnswer){

    const selectedAnswerTemp = this.question.answers.find(ans => ans.answerId === selectedAnswer.answerId)
    if(selectedAnswerTemp === undefined) {
      throw throwError('Answer was not found')
    }

    const responseAnswerUpdateRequest : ResponseAnswerUpdateRequest = {
      responseId: this._sharedDataService.questionnaireResponse!.responseId,
      question: {
        questionId: this.question.questionId,
        responseText: "",
        answers: [
          {
            ...selectedAnswerTemp,
            selected: true,
            responseTimestamp: DateUtil.toISOString(new Date())
          }
        ]
      },
      isPreview: this.isPreview
    }

    this._responseService.updateSingleAnswerForResponse(responseAnswerUpdateRequest).subscribe(
      () => {
        // update global questions
        this.question.answers = this.question.answers.map(ans => {
          ans.selected = ans.answerId === selectedAnswerTemp.answerId
          return ans
        })
        this.question.isAnswered = true;
        this.dropDownSaved.emit()
      }
    )
  }

  /**
   * 
   * @param selectedValue 
   * @returns 
   */
  displaySelection(selectedValue: QuestionnaireResponseAnswer | string) : string {   
     if(selectedValue === null){
        return "";
     } else if (typeof selectedValue === typeof "string" ) {
        return (selectedValue as string) ? (selectedValue as string) : "";
     } else {
        return (selectedValue as QuestionnaireResponseAnswer).content.text ? (selectedValue as QuestionnaireResponseAnswer).content.text : "";
     }
  }

  /**
   * 
   */
  clearSelectedValue() {
    this.myControl.setValue('');    
    this.displaySelection('');
    this.selectedOption = null;
    this.myControl.setValue(null);
    this.autocomplete.options.forEach(item => {
      item.deselect();
    });
    this.question.isAnswered = false
  }


}
